// services/manpowerTransfer.service.ts

import { 
    doc, 
    getDoc, 
    getDocs,           // 🆕 ADD THIS
    updateDoc, 
    Timestamp,
    collection,
    addDoc,
    QueryDocumentSnapshot  // 🆕 ADD THIS for type safety
  } from 'firebase/firestore';
  import { db } from './firebase';
  import { ManpowerEmployee } from './manpower.service';
  
  export interface TransferRequest {
    employeeIds: string[]; // Array of empNumbers (EMP2753, EMP2754, etc.)
    sourceProject: string;
    targetProject: string;
    targetProjectCode: string;
    targetSite: string;
    targetPosition?: string;
    remarks: string;
    transferDate: string; // "2025-12-12"
    transferredBy: string; // User ID
    transferredByName: string; // User name
  }
  
  export interface TransferLog {
    id: string;
    employeeId: string;
    employeeName: string;
    sourceProject: string;
    targetProject: string;
    targetProjectCode: string;
    targetSite: string;
    remarks: string;
    transferDate: string;
    transferredAt: Timestamp;
    transferredBy: string;
    transferredByName: string;
  }
  
  export interface TransferValidation {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    employeeDetails: Array<{
      empNumber: string;
      name: string;
      currentProject: string;
      canTransfer: boolean;
      reason?: string;
    }>;
  }
  
  class ManpowerTransferService {
    private readonly COLLECTION_PATH = 'Manpower Allocation';
    private readonly TRANSFER_LOGS_PATH = 'Manpower_Transfer_Logs';
  
    /**
     * Validate transfer request before execution
     */
    async validateTransfer(request: TransferRequest): Promise<TransferValidation> {
      const validation: TransferValidation = {
        isValid: true,
        errors: [],
        warnings: [],
        employeeDetails: []
      };
  
      try {
        // 1. Check if target project exists
        const targetProjectDoc = await getDoc(
          doc(db, this.COLLECTION_PATH, request.targetProject)
        );
  
        if (!targetProjectDoc.exists()) {
          validation.isValid = false;
          validation.errors.push(`Target project "${request.targetProject}" does not exist`);
          return validation;
        }
  
        // 2. Check each employee
        for (const empNumber of request.employeeIds) {
          // Find employee in source project
          const sourceProjectDoc = await getDoc(
            doc(db, this.COLLECTION_PATH, request.sourceProject)
          );
  
          if (!sourceProjectDoc.exists()) {
            validation.isValid = false;
            validation.errors.push(`Source project "${request.sourceProject}" not found`);
            continue;
          }
  
          const sourceData = sourceProjectDoc.data();
          const employees = sourceData.employees || [];
          
          const employee = employees.find((emp: ManpowerEmployee) => 
            emp.empNumber === empNumber
          );
  
          if (!employee) {
            validation.isValid = false;
            validation.errors.push(`Employee ${empNumber} not found in ${request.sourceProject}`);
            validation.employeeDetails.push({
              empNumber,
              name: 'Unknown',
              currentProject: request.sourceProject,
              canTransfer: false,
              reason: 'Employee not found in source project'
            });
            continue;
          }
  
          // Check if already in target project
          const targetData = targetProjectDoc.data();
          const targetEmployees = targetData.employees || [];
          const alreadyInTarget = targetEmployees.some((emp: ManpowerEmployee) => 
            emp.empNumber === empNumber
          );
  
          if (alreadyInTarget) {
            validation.isValid = false;
            validation.errors.push(
              `${employee.name} (${empNumber}) is already in ${request.targetProject}`
            );
            validation.employeeDetails.push({
              empNumber,
              name: employee.name,
              currentProject: request.sourceProject,
              canTransfer: false,
              reason: 'Already in target project'
            });
            continue;
          }
  
          // Valid employee
          validation.employeeDetails.push({
            empNumber,
            name: employee.name,
            currentProject: request.sourceProject,
            canTransfer: true
          });
        }
  
        // 3. Check if any employees can be transferred
        if (validation.employeeDetails.filter(e => e.canTransfer).length === 0) {
          validation.isValid = false;
          validation.errors.push('No employees can be transferred');
        }
  
      } catch (error) {
        console.error('Transfer validation error:', error);
        validation.isValid = false;
        validation.errors.push(
          `Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
  
      return validation;
    }
  
    /**
     * Execute employee transfer
     */
    async transferEmployees(request: TransferRequest): Promise<{
      success: boolean;
      transferredCount: number;
      failedCount: number;
      logs: TransferLog[];
      errors: string[];
    }> {
      const result = {
        success: false,
        transferredCount: 0,
        failedCount: 0,
        logs: [] as TransferLog[],
        errors: [] as string[]
      };
  
      try {
        // Validate first
        const validation = await this.validateTransfer(request);
        
        if (!validation.isValid) {
          result.errors = validation.errors;
          return result;
        }
  
        // Get source and target projects
        const sourceProjectDoc = await getDoc(
          doc(db, this.COLLECTION_PATH, request.sourceProject)
        );
        const targetProjectDoc = await getDoc(
          doc(db, this.COLLECTION_PATH, request.targetProject)
        );
  
        if (!sourceProjectDoc.exists() || !targetProjectDoc.exists()) {
          result.errors.push('Source or target project not found');
          return result;
        }
  
        const sourceData = sourceProjectDoc.data();
        const targetData = targetProjectDoc.data();
        
        let sourceEmployees = sourceData.employees || [];
        let targetEmployees = targetData.employees || [];
  
        // Transfer each valid employee
        for (const detail of validation.employeeDetails) {
          if (!detail.canTransfer) {
            result.failedCount++;
            continue;
          }
  
          // Find employee in source
          const employeeIndex = sourceEmployees.findIndex(
            (emp: ManpowerEmployee) => emp.empNumber === detail.empNumber
          );
  
          if (employeeIndex === -1) {
            result.failedCount++;
            result.errors.push(`Employee ${detail.empNumber} not found`);
            continue;
          }
  
          // Get employee data
          const employee = sourceEmployees[employeeIndex];
  
          // Remove from source
          sourceEmployees = sourceEmployees.filter(
            (emp: ManpowerEmployee) => emp.empNumber !== detail.empNumber
          );
  
          // Update employee data for target project
          const updatedEmployee: ManpowerEmployee = {
            ...employee,
            projectCode: request.targetProjectCode,
            projectName: request.targetProject,
            site: request.targetSite,
            position: request.targetPosition || employee.position,
            addedAt: Timestamp.now(),
            addedBy: request.transferredBy
          };
  
          // Add to target
          targetEmployees.push(updatedEmployee);
  
          // Create transfer log
          const transferLog: Omit<TransferLog, 'id'> = {
            employeeId: employee.empNumber,
            employeeName: employee.name,
            sourceProject: request.sourceProject,
            targetProject: request.targetProject,
            targetProjectCode: request.targetProjectCode,
            targetSite: request.targetSite,
            remarks: request.remarks,
            transferDate: request.transferDate,
            transferredAt: Timestamp.now(),
            transferredBy: request.transferredBy,
            transferredByName: request.transferredByName
          };
  
          // Save log to Firestore
          const logRef = await addDoc(
            collection(db, this.TRANSFER_LOGS_PATH),
            transferLog
          );
  
          result.logs.push({
            ...transferLog,
            id: logRef.id
          });
  
          result.transferredCount++;
        }
  
        // Update source project
        await updateDoc(doc(db, this.COLLECTION_PATH, request.sourceProject), {
          employees: sourceEmployees,
          updatedAt: Timestamp.now(),
          lastModifiedBy: request.transferredBy
        });
  
        // Update target project
        await updateDoc(doc(db, this.COLLECTION_PATH, request.targetProject), {
          employees: targetEmployees,
          updatedAt: Timestamp.now(),
          lastModifiedBy: request.transferredBy
        });
  
        result.success = true;
        console.log(`✅ Transferred ${result.transferredCount} employees from ${request.sourceProject} to ${request.targetProject}`);
  
      } catch (error) {
        console.error('Transfer execution error:', error);
        result.errors.push(
          `Transfer failed: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
  
      return result;
    }
  
    /**
     * Get transfer logs for an employee
     */
    async getEmployeeTransferHistory(empNumber: string): Promise<TransferLog[]> {
      try {
        const logsRef = collection(db, this.TRANSFER_LOGS_PATH);
        const snapshot = await getDocs(logsRef);
        
        const logs: TransferLog[] = [];
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.employeeId === empNumber) {
            logs.push({
              id: doc.id,
              ...data
            } as TransferLog);
          }
        });
  
        // Sort by transfer date descending
        logs.sort((a, b) => 
          b.transferredAt.toMillis() - a.transferredAt.toMillis()
        );
  
        return logs;
      } catch (error) {
        console.error('Error fetching transfer history:', error);
        return [];
      }
    }
  
    /**
     * Get all transfer logs for a date range
     */
    async getTransferLogs(startDate: string, endDate: string): Promise<TransferLog[]> {
      try {
        const logsRef = collection(db, this.TRANSFER_LOGS_PATH);
        const snapshot = await getDocs(logsRef);
        
        const logs: TransferLog[] = [];
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const transferDate = data.transferDate;
          
          if (transferDate >= startDate && transferDate <= endDate) {
            logs.push({
              id: doc.id,
              ...data
            } as TransferLog);
          }
        });
  
        logs.sort((a, b) => 
          b.transferredAt.toMillis() - a.transferredAt.toMillis()
        );
  
        return logs;
      } catch (error) {
        console.error('Error fetching transfer logs:', error);
        return [];
      }
    }
  }
  
  export const manpowerTransferService = new ManpowerTransferService();